 
import * as React from "react";
import { format, getYear, getMonth, setMonth, setYear, startOfDay, startOfMonth } from "date-fns";
import { Calendar as CalendarIcon, Clock } from "lucide-react";

import { cn } from "../lib/utils";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";
import { Button } from "./button";

type DatePickerVariant = "basic" | "yearMonth" | "range" | "dualMonth";

interface DatePickerProps {
  // For single date selection
  date?: Date;
  setDate?: (date: Date | undefined) => void;
  
  // For date range selection
  dateRange?: {
    from: Date | undefined;
    to: Date | undefined;
  };
  setDateRange?: (dateRange: {
    from: Date | undefined;
    to: Date | undefined;
  }) => void;
  
  // Common props
  placeholder?: string;
  format?: string;
  disabled?: boolean;
  className?: string;
  variant?: DatePickerVariant;
  showTimePicker?: boolean;
  /**
   * The earliest date that may be picked. Anything before it is greyed out and
   * cannot be clicked — a calendar that offers last Tuesday for a meeting is
   * offering a choice the form will refuse, and only says so on submit.
   *
   * Compared by day, so passing "now" still allows today.
   */
  minDate?: Date;
  /** The latest date that may be picked, compared by day like `minDate`. */
  maxDate?: Date;
  /** Forwarded to the trigger button, so a form label can point at it. */
  id?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

export function DatePicker({
  date,
  setDate,
  dateRange,
  setDateRange,
  placeholder = "Select date",
  format: dateFormat = "PPP",
  disabled = false,
  className,
  variant = "basic",
  showTimePicker = false,
  minDate,
  maxDate,
  id,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [yearViewDate, setYearViewDate] = React.useState(date || new Date());
  
  // Time picker state
  const [hours, setHours] = React.useState<string>("12");
  const [minutes, setMinutes] = React.useState<string>("00");
  const [ampm, setAmpm] = React.useState<string>("AM");

  // Day-precision, so a `minDate` of "now" leaves the whole of today open
  // rather than half of it.
  const disabledDays = React.useMemo(() => {
    if (!minDate && !maxDate) return undefined;
    const rules: ({ before: Date } | { after: Date })[] = [];
    if (minDate) rules.push({ before: startOfDay(minDate) });
    if (maxDate) rules.push({ after: startOfDay(maxDate) });
    return rules;
  }, [minDate, maxDate]);

  // Generate years for select (10 years before and after current year)
  const currentYear = getYear(new Date());
  const allYears = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);
  // A year the calendar's own minDate already refuses to land on shouldn't
  // be offered here either — same rule the day grid enforces, one level up.
  const years = minDate ? allYears.filter((year) => year >= getYear(minDate)) : allYears;

  // Generate months for select
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  // Within the minDate's own year, months before it are equally unreachable.
  // The ‹ › arrows may only go where the Year/Month selects can follow —
  // arrowing to a month the selects don't list left them showing blank.
  const firstMonth = minDate
    ? startOfMonth(minDate)
    : new Date(years[0], 0, 1);
  const lastMonth = new Date(years[years.length - 1], 11, 1);

  const monthOptions =
    minDate && getYear(yearViewDate) === getYear(minDate)
      ? months.filter((_, index) => index >= getMonth(minDate))
      : months;
  
  // Generate hours, minutes for time picker
  const hourOptions = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
  const minuteOptions = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

  // Update yearViewDate when date changes
  React.useEffect(() => {
    if (date) {
      setYearViewDate(date);
    }
  }, [date]);

  // Handle year change
  const handleYearChange = (year: string) => {
    let newDate = setYear(yearViewDate, parseInt(year));
    // Landing on minDate's year can leave the previously-selected month
    // behind it (e.g. viewing March, jump to the current year in September)
    // — pull the month forward rather than showing an unreachable page.
    if (minDate && getYear(newDate) === getYear(minDate) && getMonth(newDate) < getMonth(minDate)) {
      newDate = setMonth(newDate, getMonth(minDate));
    }
    setYearViewDate(newDate);
  };

  // Handle month change
  const handleMonthChange = (month: string) => {
    const monthIndex = months.findIndex((m) => m === month);
    if (monthIndex !== -1) {
      const newDate = setMonth(yearViewDate, monthIndex);
      setYearViewDate(newDate);
    }
  };
  
  // Format the displayed date based on variant
  const getInputValue = () => {
    if ((variant === "range" || variant === "dualMonth") && dateRange) {
      const fromDate = dateRange.from ? format(dateRange.from, dateFormat) : "";
      const toDate = dateRange.to ? format(dateRange.to, dateFormat) : "";
      
      if (fromDate && toDate) {
        return `${fromDate} - ${toDate}`;
      } else if (fromDate) {
        return fromDate;
      }
      return "";
    }
    
    return date ? format(date, dateFormat) : "";
  };

  // Render calendar content based on variant
  const renderCalendarContent = () => {
    switch (variant) {
      case "yearMonth":
        return (
          <>
            <div className="flex justify-between p-3 border-b">
              <Select
                value={getYear(yearViewDate).toString()}
                onValueChange={handleYearChange}
              >
                <SelectTrigger 
                  className="w-[120px] border border-stroke-500 hover:border-primary hover:border-[1px] focus:border-primary focus:border-[1px]" 
                  variant="outlined"
                >
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={months[getMonth(yearViewDate)]}
                onValueChange={handleMonthChange}
              >
                <SelectTrigger 
                  className="w-[120px] border border-stroke-500 hover:border-primary hover:border-[1px] focus:border-primary focus:border-[1px]" 
                  variant="outlined"
                >
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((month) => (
                    <SelectItem key={month} value={month}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Controlled by the same yearViewDate the Year/Month selects
                read, so the grid opens on the selected date's month (not
                today's) and the arrows keep the selects in step. */}
            <Calendar
              mode="single"
              selected={date}
              disabled={disabledDays}
              month={yearViewDate}
              onMonthChange={setYearViewDate}
              startMonth={firstMonth}
              endMonth={lastMonth}
              onSelect={(date) => {
                setDate?.(date);
                setOpen(false);
              }}
              initialFocus
            />
          </>
        );
        
      case "range":
        return (
          <>
            <Calendar
              mode="range"
              selected={{
                from: dateRange?.from,
                to: dateRange?.to,
              }}
              onSelect={(range) => {
                setDateRange?.({
                  from: range?.from,
                  to: range?.to,
                });
                if (range?.to) {
                  // Only close if end date is selected
                  if (!showTimePicker) {
                    setOpen(false);
                  }
                }
              }}
              defaultMonth={dateRange?.from}
              initialFocus
              numberOfMonths={1}
            />
            
            {showTimePicker && (
              <div className="p-3 border-t">
                <div className="flex items-center mb-2">
                  <Clock className="h-4 w-4 mr-2" />
                  <span className="text-sm font-medium">Time</span>
                </div>
                <div className="flex space-x-2">
                  <Select value={hours} onValueChange={setHours}>
                    <SelectTrigger 
                      className="w-[70px] border border-stroke-500 hover:border-primary hover:border-[1px] focus:border-primary focus:border-[1px]" 
                      variant="outlined"
                    >
                      <SelectValue placeholder="HH" />
                    </SelectTrigger>
                    <SelectContent>
                      {hourOptions.map((hour) => (
                        <SelectItem key={hour} value={hour}>
                          {hour}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="flex items-center">:</span>
                  <Select value={minutes} onValueChange={setMinutes}>
                    <SelectTrigger 
                      className="w-[70px] border border-stroke-500 hover:border-primary hover:border-[1px] focus:border-primary focus:border-[1px]" 
                      variant="outlined"
                    >
                      <SelectValue placeholder="MM" />
                    </SelectTrigger>
                    <SelectContent>
                      {minuteOptions.map((minute) => (
                        <SelectItem key={minute} value={minute}>
                          {minute}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={ampm} onValueChange={setAmpm}>
                    <SelectTrigger 
                      className="w-[70px] border border-stroke-500 hover:border-primary hover:border-[1px] focus:border-primary focus:border-[1px]" 
                      variant="outlined"
                    >
                      <SelectValue placeholder="AM/PM" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AM">AM</SelectItem>
                      <SelectItem value="PM">PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <button
                  type="button" 
                  className="w-full mt-3 flex justify-center items-center gap-2 px-3 py-2 h-9 text-sm bg-primary text-primary-foreground shadow-xs transition-all duration-200 rounded-md hover:bg-primary/90 focus:ring-[3px] focus:ring-secondary outline-none" 
                  onClick={() => setOpen(false)}
                >
                  Apply
                </button>
              </div>
            )}
          </>
        );
        
      case "dualMonth":
        return (
          <div className="space-y-4">
            <Calendar
              mode="range"
              selected={{
                from: dateRange?.from,
                to: dateRange?.to,
              }}
              onSelect={(range) => {
                setDateRange?.({
                  from: range?.from,
                  to: range?.to,
                });
                if (range?.to && !showTimePicker) {
                  setOpen(false);
                }
              }}
              defaultMonth={dateRange?.from}
              initialFocus
              numberOfMonths={2}
              className="dual-month-range"
              classNames={{
                months: "flex flex-row space-x-8",
                day_range_start: "bg-transparent border border-primary text-primary font-bold rounded-l-md",
                day_range_end: "bg-transparent border border-primary text-primary font-bold rounded-r-md",
                day_range_middle: "bg-secondary hover:bg-secondary/60 rounded-none text-secondary-foreground",
                day_selected: "bg-primary text-white-both-modes",
                day_today: "!bg-transparent !border !border-primary !text-primary"
              }}
            />
          </div>
        );
        
      case "basic":
      default:
        return (
          <Calendar
            mode="single"
            selected={date}
            defaultMonth={date}
            disabled={disabledDays}
            onSelect={(date) => {
              setDate?.(date);
              setOpen(false);
            }}
            initialFocus
          />
        );
    }
  };

  // State for tracking focus type and interaction method
  const [focused, setFocused] = React.useState(false)
  const [keyboardFocus, setKeyboardFocus] = React.useState(false)
  const [lastInteractionWasKeyboard, setLastInteractionWasKeyboard] = React.useState(false)
  
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        setLastInteractionWasKeyboard(true)
      }
    }

    const handleMouseDown = () => {
      setLastInteractionWasKeyboard(false)
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('mousedown', handleMouseDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('mousedown', handleMouseDown)
    }
  }, [])

  // Focus handlers
  const handleFocus = () => {
    setFocused(true)
    setKeyboardFocus(lastInteractionWasKeyboard)
  }

  const handleBlur = () => {
    setFocused(false)
    setKeyboardFocus(false)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        // Re-anchor on every open: arrowing away and closing without picking
        // shouldn't leave the next open parked on that other month.
        if (next) setYearViewDate(date || new Date());
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <Button
        variant={'outline'}
          type="button"
          id={id}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={cn(
            // Base classes
            "group peer px-3 py-2 h-11 flex w-full min-w-0 text-base shadow-xs transition-all duration-200 outline-none md:text-sm file:hidden",
            "justify-start text-left font-normal gap-2",
            "bg-transparent rounded-md border border-stroke-500", 
            
            // Hover styles
            "hover:border-primary hover:border-[1px]", 
            
            // Focus styles
            "focus:border-primary focus:border-[1px]", 
            
            // Regular focus (mouse click)
            focused && !keyboardFocus && "ring-[0px] ring-secondary/50",
            
            // Keyboard focus (tab key)
            keyboardFocus && "ring-[3px] ring-secondary",
            
            // States
            !date && !dateRange?.from && "text-muted-foreground",
            disabled && "opacity-50 cursor-not-allowed",
            open && "ring-1 ring-secondary",
            
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="h-4 w-4 text-primary group-hover:text-primary group-focus-within:text-primary transition-colors duration-200" />
          <span className="flex-grow">
            {date || (dateRange && (dateRange.from || dateRange.to)) ? (
              getInputValue()
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn(
        "w-auto p-0 border border-stroke-500 shadow-md rounded-md bg-background", 
        variant === "dualMonth" && "w-[560px]"
      )} align="start">
        {renderCalendarContent()}
      </PopoverContent>
    </Popover>
  );
}
